// Slide-in notifications drawer.
// Q12 S5: rows reshape to <div role="button"> with inner ✓/✗ icon-buttons
// (D7a). Body click navigates per buildUrl (D11b: navigate-first,
// fire-and-forget mark-read; D12a: close drawer on internal navigate;
// external links open in a new tab and the drawer stays open). ✓ click
// marks read only. ✗ click dismisses optimistically (PATCH /:id/dismiss
// from S2; restores on failure). Action wiring lives in
// useNotificationActions to keep this component under the line-count cap.

import type { JSX } from "react";
import type { NotificationLink } from "@hcm/core";
import { useNotificationActions } from "../hooks/useNotificationActions.js";
import { NotificationRow } from "./NotificationRow.js";
import type { AlertSeverity } from "../lib/notification-severity.js";

export interface Notification {
  _id: string;
  event_type: string;
  severity?: AlertSeverity | null;
  title: string;
  body: string;
  created_at: string;
  read_at: string | null;
  dismissed_at?: string | null;
  link?: NotificationLink | null;
  payload?: Record<string, unknown>;
}

interface Props {
  open: boolean;
  onClose: () => void;
  items: Notification[];
}

export function NotificationsDrawer({ open, onClose, items }: Props): JSX.Element {
  const { visible, handleActivate, handleMarkRead, handleDismiss, markAllRead } = useNotificationActions(items, onClose);

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} aria-hidden="true" />
      )}
      <div
        className={`fixed right-0 top-0 h-full w-80 bg-white shadow-xl border-l border-slate-200 z-50 flex flex-col transition-transform duration-200 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        role="dialog"
        aria-label="Notifications"
      >
        <div className="flex items-center justify-between px-4 py-4 border-b border-slate-200 shrink-0">
          <h2 className="text-sm font-semibold text-slate-800">Notifications</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-lg leading-none"
            aria-label="Close notifications"
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {visible.length === 0 && (
            <p className="py-12 text-center text-sm text-slate-400">No notifications</p>
          )}
          {visible.map((n) => (
            <NotificationRow
              key={n._id}
              item={n}
              onActivate={handleActivate}
              onMarkRead={handleMarkRead}
              onDismiss={handleDismiss}
            />
          ))}
        </div>
        <div className="shrink-0 border-t border-slate-100 px-4 py-3">
          <button
            onClick={() => { void markAllRead(); }}
            className="w-full text-xs text-navy-700 hover:text-navy-900 font-medium transition-colors"
          >
            Mark all read
          </button>
        </div>
      </div>
    </>
  );
}
